'use client'

/***
 *
 * @file layouts/Container/index.tsx
 * @description Container layout will be used for rendering the content.
 * @author Naveen Kumar
 * @organization Ingka Center
 * @since 13 Feb 2024
 *
 */

import React, { ImgHTMLAttributes, ReactNode } from "react";
// import "./Container.scss";
import { StyledContainer } from "@/shared/StyledComponents";
import classNames from "classnames";
import { srcPath } from "@/helpers/media.helper";

/** defines type of props passed to Container functional component */
type T_Props = ImgHTMLAttributes<HTMLImageElement> &  {
  /** children components to be rendered within the Container layout */
  children: ReactNode,

  /** Unique identifier for a Container */
  id?: string,

  /** CSS class to be passed inside the Container div component */
  className?: string,

  /** CSS styles to be passed in the style attribute of the Container div component */
  style?: React.CSSProperties,
  padding?: string,
  width?: string,
  height?: string,
  bgMedia?: string;
  imagePositionX?: string;
  imagePositionY?: string;
  bgSize: string;
  bgPosition: string;
  name?: string;
  // Required for Studio highlighting
  'data-cf-node-id'?: string;
  'data-cf-node-block-id'?: string;
  'data-cf-node-block-type'?: string;
  experienceProps?: Record<string, any>;
  // cssClassName?: string;
}

/**
 * Name: Container
 * 
 * Description: It takes in children as props and renders them within the div component as Container layout
 * 
 * @param param0 object containing the children to be rendered and other props
 * @example Parameter breakup
 * ```
 *  param0: {
 *    children,             //children components to be rendered
 *    className: "",        //className string is passed via contentful
 *    numberOfContainers: ""   //accepts number of Containers to render for proper styling
 *    customStyles: {},     //style object passed via contentful
 *    id: ""             
 *  }
 * ```
 * @example T_Props
 * ```
 *  type T_Props = {
 *      children: ReactNode,        
 *      id?: string,                // optional
 *      numberOfContainers?: number,   // optional
 *      className?: string,         // optional
 *      customStyles?: any          // optional
 * }
 * ```
 * @returns an html that renders child components within an html div element as part of a Container layout
 */
const Container = ({ children, ...props }: T_Props) => {
  const {
    width,
    height,
    bgPosition = 'center center',
    bgSize = 'cover',
    ...rest
  } = props


  return (
    <StyledContainer {...props} style={{position: "relative", height: "100%", width: "100%"}}>
    <div 
      data-testid="Container"
      id={rest?.id || "clm"} 
      title={rest?.name}
      aria-label={rest?.name}
      {...rest?.experienceProps} // Critical: passes data-cf-* attributes
      className={classNames(
        'flex',
        'track-section',
        `bp-container-0`, 
          `Container`, 
          `w-screen`,
          rest?.className,
          // rest?.cssClassName
      )}
      style={{
        position: "relative", 
        width, 
        height: height || "100%", 
        // minWidth: "100%",
        // backgroundColor: "red",
        // minHeight: '20px', // Required for hover detection
        ...(()=>{
          if(rest?.bgMedia?.slice(2) && rest?.bgMedia?.slice(2)) {
            return {
              backgroundPosition:`${bgPosition}`, 
              backgroundSize: `${bgSize}`, 
              backgroundRepeat: 'no-repeat', 
              backgroundImage: `url(https://${srcPath({src: rest?.bgMedia?.slice(2)})}`
            };
          } else {
            return {};
          }
        })(), 
        ...rest?.style
      }}
    >
      {/* <div id={props?.id || "clm"} 
        title={props?.name}
        aria-label={props?.name}
        className={classNames(`bp-container-0`, 
          `Container`, 
          `w-screen`,
          // `!bg-${bgSize}`,
          // `!bg-${bgPosition}`, 
          props?.className
        // )} data-testid="Container" style={{ width, height, backgroundPosition: `${imagePositionX} ${imagePositionY}`, backgroundRepeat: 'no-repeat', backgroundImage: `url(https://${props.bgMedia?.slice(2)})`, ...props?.style }}>
        )} data-testid="Container" style={{ width, height, backgroundPosition:`${bgPosition}`, backgroundSize: `${bgSize}` , backgroundRepeat: 'no-repeat', backgroundImage: `url(https://${srcPath({src: props.bgMedia?.slice(2)})}`, ...props?.style }}> */}
        {/* <pre style={{padding: 20, background: "#fff"}}>{JSON.stringify(props, null, 2)}</pre> */}
        {children}
      </div>
    </StyledContainer>
  );
};

export default Container;
